import { supabase } from '@/lib/supabase';
import { callEdgeFunction } from '@/lib/edgeFunctions';
import { ServiceError, unwrap } from '@/data/serviceError';
import type { Tables } from '@/types/db';
import type { KlikContext } from '@/lib/gebruiksEvents';
import type { Beoordeling, DoorvraagVraag } from '../lib/meldingStatus';
import type { MeldingSoort } from '../lib/meldingFormulier';

/** Wat de AI nodig heeft om te beoordelen of een melding naspeelbaar is. */
export interface DuidelijkheidInvoer {
  sessieId: string | null;
  ronde: number;
  soort: MeldingSoort;
  omschrijving: string;
  stappen?: string | null;
  ernst?: string | null;
  scherm?: string | null;
  url?: string | null;
  kliks: KlikContext[];
  fouten: { tijd: string; melding: string }[];
  heeftSchermafbeelding: boolean;
  /** Alleen in de eerste ronde meesturen (kosten). */
  screenshotBase64?: string;
  eerdereVragen: DoorvraagVraag[];
}

const DUIDELIJKHEID_TIJDSLIMIET_MS = 25_000;

export type OnvolledigeMelding = Pick<
  Tables<'software_meldingen'>,
  | 'id'
  | 'soort'
  | 'omschrijving'
  | 'scherm'
  | 'status'
  | 'doorvraag'
  | 'onvolledig_sinds'
  | 'created_at'
>;

/**
 * Data-toegang rond de duidelijkheid van meldingen: de AI-check (edge
 * function, sleutel blijft server-side), de vraag-terug van Tom en het
 * antwoord van de melder (RPC's met eigen rechtencontrole in de database).
 */
export const meldingenService = {
  /**
   * Beoordeel of de melding duidelijk genoeg is. Faalt de aanroep of duurt
   * hij te lang, dan geldt "duidelijk" met een fout-markering: de melding mag
   * nooit stranden op de check.
   */
  async beoordeelDuidelijkheid(invoer: DuidelijkheidInvoer): Promise<Beoordeling> {
    try {
      const antwoord = (await Promise.race([
        callEdgeFunction('melding-duidelijkheid', { ...invoer }),
        new Promise<null>((r) => setTimeout(() => r(null), DUIDELIJKHEID_TIJDSLIMIET_MS)),
      ])) as Partial<Beoordeling> | null;
      if (!antwoord)
        return { duidelijk: true, score: null, waarom: null, vragen: [], fout: 'tijdslimiet' };
      return {
        duidelijk: antwoord.duidelijk !== false,
        score: typeof antwoord.score === 'number' ? antwoord.score : null,
        waarom: typeof antwoord.waarom === 'string' ? antwoord.waarom : null,
        vragen: Array.isArray(antwoord.vragen) ? antwoord.vragen : [],
        ...(antwoord.fout ? { fout: antwoord.fout } : {}),
        ...(typeof antwoord.sessieId === 'string' ? { sessieId: antwoord.sessieId } : {}),
      };
    } catch (err) {
      return {
        duidelijk: true,
        score: null,
        waarom: null,
        vragen: [],
        fout: err instanceof Error ? err.message : 'onbekend',
      };
    }
  },

  /** Tom stelt een vraag terug: status onvolledig + notificatie voor de melder. */
  async vraagTerug(id: string, vraag: string): Promise<void> {
    const { error } = await supabase.rpc('melding_vraag_terug', { p_id: id, p_vraag: vraag });
    if (error) throw new ServiceError(`Vraag terugstellen: ${error.message}`, error.code);
  },

  /** De melder beantwoordt de open vraag bij zijn onvolledige melding. */
  async beantwoord(id: string, antwoord: string): Promise<void> {
    const { error } = await supabase.rpc('melding_vraag_beantwoorden', {
      p_id: id,
      p_antwoord: antwoord,
    });
    if (error) throw new ServiceError(`Antwoord versturen: ${error.message}`, error.code);
  },

  /** Eigen meldingen die op een aanvulling wachten (voor Mijn Inbox). */
  async mijnOnvolledige(userId: string): Promise<OnvolledigeMelding[]> {
    return unwrap(
      await supabase
        .from('software_meldingen')
        .select('id, soort, omschrijving, scherm, status, doorvraag, onvolledig_sinds, created_at')
        .eq('melder_id', userId)
        .eq('status', 'onvolledig')
        .order('onvolledig_sinds', { ascending: false }),
      'Onvolledige meldingen laden',
    );
  },
};
